import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Package, 
  Mail, 
  MailCheck, 
  MailX,
  Clock, 
  CheckCircle, 
  AlertCircle,
  RefreshCw,
  Loader2,
  ExternalLink,
  Bitcoin,
  Copy,
  Eye,
  MapPin,
  Plus,
  Trash2,
  Pencil,
  X as XIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  awaiting_payment: 'bg-yellow-100 text-yellow-800',
  payment_verification: 'bg-blue-100 text-blue-800',
  payment_confirmed: 'bg-green-100 text-green-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

const statusLabels = {
  awaiting_payment: 'Awaiting Payment',
  payment_verification: 'Verifying Payment',
  payment_confirmed: 'Payment Confirmed',
  processing: 'Processing',
  shipped: 'Shipped',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export default function AdminOrdersPage() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [trackForm, setTrackForm] = useState({ location: '', status: '', note: '' });
  const [editingEventId, setEditingEventId] = useState(null);
  const [savingTracking, setSavingTracking] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (orderId) => {
    setSendingEmail(orderId);
    try {
      await axios.post(`${API_URL}/api/orders/${orderId}/resend-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Confirmation email sent successfully');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      await axios.put(`${API_URL}/api/orders/${orderId}/status?order_status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setTrackForm({ location: '', status: '', note: '' });
    setEditingEventId(null);
    setIsDetailOpen(true);
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;
    setDeletingOrder(true);
    try {
      await axios.delete(`${API_URL}/api/orders/${orderToDelete.orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Order ${orderToDelete.orderId} deleted`);
      setOrders((prev) => prev.filter((o) => o.orderId !== orderToDelete.orderId));
      if (selectedOrder?.orderId === orderToDelete.orderId) {
        setIsDetailOpen(false);
        setSelectedOrder(null);
      }
      setOrderToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeletingOrder(false);
    }
  };

  const refreshSelectedOrder = async (orderId) => {
    try {
      const res = await axios.get(`${API_URL}/api/orders/${orderId}`);
      setSelectedOrder(res.data);
      setOrders((prev) => prev.map((o) => (o.orderId === orderId ? { ...o, ...res.data } : o)));
    } catch (error) {
      // non-fatal
    }
  };

  const handleSaveTracking = async () => {
    if (!selectedOrder) return;
    if (!trackForm.location.trim()) {
      toast.error('Location is required');
      return;
    }
    setSavingTracking(true);
    const payload = {
      location: trackForm.location.trim(),
      status: trackForm.status || null,
      note: trackForm.note.trim() || null,
    };
    try {
      if (editingEventId) {
        await axios.put(
          `${API_URL}/api/orders/${selectedOrder.orderId}/tracking/${editingEventId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Location update edited');
      } else {
        await axios.post(
          `${API_URL}/api/orders/${selectedOrder.orderId}/tracking`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Location update added');
      }
      setTrackForm({ location: '', status: '', note: '' });
      setEditingEventId(null);
      await refreshSelectedOrder(selectedOrder.orderId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location update');
    } finally {
      setSavingTracking(false);
    }
  };

  const handleEditTracking = (ev) => {
    setEditingEventId(ev.id);
    setTrackForm({ location: ev.location || '', status: ev.status || '', note: ev.note || '' });
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setTrackForm({ location: '', status: '', note: '' });
  };

  const handleDeleteTracking = async (eventId) => {
    if (!selectedOrder) return;
    setDeletingEventId(eventId);
    try {
      await axios.delete(`${API_URL}/api/orders/${selectedOrder.orderId}/tracking/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Location update removed');
      if (editingEventId === eventId) handleCancelEdit();
      await refreshSelectedOrder(selectedOrder.orderId);
    } catch (error) {
      toast.error('Failed to remove location update');
    } finally {
      setDeletingEventId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Orders</h1>
          <p className="text-slate-600">Manage customer orders and payments</p>
        </div>
        <Button variant="outline" onClick={fetchOrders} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {orders.filter(o => o.orderStatus === 'awaiting_payment').length}
              </p>
              <p className="text-sm text-slate-500">Awaiting Payment</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {orders.filter(o => o.orderStatus === 'payment_verification').length}
              </p>
              <p className="text-sm text-slate-500">Verifying</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {orders.filter(o => ['payment_confirmed', 'processing', 'shipped', 'completed'].includes(o.orderStatus)).length}
              </p>
              <p className="text-sm text-slate-500">Confirmed</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{orders.length}</p>
              <p className="text-sm text-slate-500">Total Orders</p>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="hidden md:table-cell">Engine</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">Email</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <div className="h-12 bg-slate-100 rounded animate-pulse" />
                  </TableCell>
                </TableRow>
              ))
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  No orders yet
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.orderId}>
                  <TableCell>
                    <span className="font-mono text-sm font-medium">{order.orderId}</span>
                    <p className="text-xs text-slate-500">{formatDate(order.created_at)}</p>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{order.buyerInfo?.name}</p>
                    <p className="text-sm text-slate-500">{order.buyerInfo?.email}</p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <p className="text-sm truncate max-w-[200px]" title={order.engineTitle}>
                      {order.engineTitle}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="font-semibold">{formatCurrency(order.totalUsd)}</p>
                    <p className="text-xs text-orange-600 flex items-center gap-1">
                      <Bitcoin className="w-3 h-3" />
                      {order.btcAmount} BTC
                    </p>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={order.orderStatus}
                      onValueChange={(value) => handleStatusChange(order.orderId, value)}
                      disabled={updatingStatus === order.orderId}
                    >
                      <SelectTrigger className={`w-[160px] h-8 text-xs ${statusColors[order.orderStatus]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(statusLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {order.emailSent ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <MailCheck className="w-4 h-4" />
                        <span className="text-xs">Sent</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-slate-400">
                        <MailX className="w-4 h-4" />
                        <span className="text-xs">Not sent</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => viewOrderDetails(order)}
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleResendEmail(order.orderId)}
                        disabled={sendingEmail === order.orderId}
                        title="Send/Resend email"
                      >
                        {sendingEmail === order.orderId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setOrderToDelete(order)}
                        title="Delete order"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-order-${order.orderId}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Order Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <DialogDescription>
              Order {selectedOrder?.orderId}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-2">Customer</h4>
                  <p className="text-sm">{selectedOrder.buyerInfo?.name}</p>
                  <p className="text-sm text-slate-500">{selectedOrder.buyerInfo?.email}</p>
                  <p className="text-sm text-slate-500">{selectedOrder.buyerInfo?.phone}</p>
                  {selectedOrder.buyerInfo?.company && (
                    <p className="text-sm text-slate-500">{selectedOrder.buyerInfo?.company}</p>
                  )}
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-2">Shipping Address</h4>
                  <p className="text-sm">{selectedOrder.buyerInfo?.shippingAddress}</p>
                  <p className="text-sm text-slate-500">
                    {selectedOrder.buyerInfo?.shippingCity}, {selectedOrder.buyerInfo?.shippingCountry}
                  </p>
                  <p className="text-sm text-slate-500">{selectedOrder.buyerInfo?.shippingRegion}</p>
                </div>
              </div>

              {/* Order Info */}
              <div className="bg-orange-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-3">Payment Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Engine Price:</span>
                    <span className="ml-2 font-medium">{formatCurrency(selectedOrder.enginePrice || 0)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Shipping:</span>
                    <span className="ml-2 font-medium">{formatCurrency(selectedOrder.shippingCost || 0)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Total USD:</span>
                    <span className="ml-2 font-semibold text-lg">{formatCurrency(selectedOrder.totalUsd)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">BTC Amount:</span>
                    <span className="ml-2 font-semibold text-orange-600">{selectedOrder.btcAmount} BTC</span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-orange-200">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">BTC Address:</span>
                    <code className="flex-1 bg-white px-2 py-1 rounded text-xs font-mono truncate">
                      {selectedOrder.btcAddress}
                    </code>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => copyToClipboard(selectedOrder.btcAddress)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  {selectedOrder.txHash && (
                    <div className="flex items-center gap-2 text-sm mt-2">
                      <span className="text-slate-500">TX Hash:</span>
                      <code className="flex-1 bg-white px-2 py-1 rounded text-xs font-mono truncate">
                        {selectedOrder.txHash}
                      </code>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => window.open(`https://www.blockchain.com/btc/tx/${selectedOrder.txHash}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Email Status */}
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  {selectedOrder.emailSent ? (
                    <>
                      <MailCheck className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-700">Email Sent</p>
                        {selectedOrder.emailSentAt && (
                          <p className="text-xs text-slate-500">{formatDate(selectedOrder.emailSentAt)}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <MailX className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-600">Email Not Sent</p>
                        <p className="text-xs text-slate-500">Check SMTP settings</p>
                      </div>
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleResendEmail(selectedOrder.orderId)}
                  disabled={sendingEmail === selectedOrder.orderId}
                >
                  {sendingEmail === selectedOrder.orderId ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {selectedOrder.emailSent ? 'Resend Email' : 'Send Email'}
                </Button>
              </div>

              {/* Notes */}
              {selectedOrder.buyerInfo?.notes && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-900 mb-2">Customer Notes</h4>
                  <p className="text-sm text-slate-600">{selectedOrder.buyerInfo.notes}</p>
                </div>
              )}

              {/* Tracking / Location Updates */}
              <div className="border border-slate-200 rounded-lg p-4" data-testid="admin-tracking-section">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-500" />
                  Tracking & Location Updates
                </h4>

                {/* Existing events */}
                {selectedOrder.trackingEvents && selectedOrder.trackingEvents.length > 0 ? (
                  <ol className="relative border-l-2 border-slate-100 ml-1 mb-4">
                    {[...selectedOrder.trackingEvents]
                      .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))
                      .map((ev) => (
                        <li key={ev.id} className="mb-4 last:mb-0 ml-4" data-testid={`tracking-event-${ev.id}`}>
                          <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-orange-500" />
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-slate-800 text-sm">{ev.location}</p>
                                {ev.status && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {statusLabels[ev.status] || ev.status}
                                  </Badge>
                                )}
                              </div>
                              {ev.note && <p className="text-xs text-slate-600 mt-0.5">{ev.note}</p>}
                              <p className="text-xs text-slate-400 mt-0.5">{formatDate(ev.timestamp)}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleEditTracking(ev)}
                                title="Edit"
                                data-testid={`edit-tracking-${ev.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-red-500 hover:text-red-600"
                                onClick={() => handleDeleteTracking(ev.id)}
                                disabled={deletingEventId === ev.id}
                                title="Delete"
                                data-testid={`delete-tracking-${ev.id}`}
                              >
                                {deletingEventId === ev.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </li>
                      ))}
                  </ol>
                ) : (
                  <p className="text-sm text-slate-400 mb-4">No location updates yet.</p>
                )}

                {/* Add / Edit form */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-700">
                      {editingEventId ? 'Edit location update' : 'Add location update'}
                    </p>
                    {editingEventId && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleCancelEdit}>
                        <XIcon className="w-3.5 h-3.5 mr-1" />
                        Cancel
                      </Button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-600">Location *</Label>
                      <Input
                        placeholder="e.g. Port of Rotterdam"
                        value={trackForm.location}
                        onChange={(e) => setTrackForm({ ...trackForm, location: e.target.value })}
                        className="mt-1 h-9"
                        data-testid="tracking-location-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Status (optional)</Label>
                      <Select
                        value={trackForm.status || 'none'}
                        onValueChange={(v) => setTrackForm({ ...trackForm, status: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger className="mt-1 h-9" data-testid="tracking-status-select">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No status</SelectItem>
                          {Object.entries(statusLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Note (optional)</Label>
                    <Textarea
                      placeholder="e.g. Cleared customs, in transit to final destination"
                      value={trackForm.note}
                      onChange={(e) => setTrackForm({ ...trackForm, note: e.target.value })}
                      className="mt-1"
                      rows={2}
                      data-testid="tracking-note-input"
                    />
                  </div>
                  <Button
                    onClick={handleSaveTracking}
                    disabled={savingTracking}
                    className="btn-primary w-full sm:w-auto"
                    data-testid="save-tracking-button"
                  >
                    {savingTracking ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : editingEventId ? (
                      <Pencil className="w-4 h-4 mr-2" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    {editingEventId ? 'Save Changes' : 'Add Update'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Order Confirmation */}
      <AlertDialog open={!!orderToDelete} onOpenChange={(open) => !open && setOrderToDelete(null)}>
        <AlertDialogContent data-testid="delete-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete order{' '}
              <span className="font-mono font-medium">{orderToDelete?.orderId}</span>
              {orderToDelete?.buyerInfo?.name ? ` from ${orderToDelete.buyerInfo.name}` : ''}. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingOrder}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteOrder();
              }}
              disabled={deletingOrder}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="confirm-delete-order-button"
            >
              {deletingOrder ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
